import { AccordionHeaderTitle } from 'component/Accordion';
import React, { useEffect, useRef, useState } from 'react';
import { AccordionItemButton, AccordionItemHeading, AccordionItemPanel } from 'react-accessible-accordion';
import { LocationComponent } from 'pages/Reports/blocks/Location/Location';
import { SettlementId } from 'pages/Reports/blocks/Location/SettlementId';
import { NTSmap } from 'pages/Reports/blocks/NTSmap';
import { useHAReports } from 'hooks/useHAReports';
import { SessionDate } from './SessionDate';
import {
  CriticalInfrastructure,
  EducationFacility,
  PlaceTypeOfImsma,
  PlaceTypeOfInternal,
  StateInstitution,
} from './Place';
import { TypeOfSessionImsma, TypeOfSessionInternal } from './TypeOfSession';
import { DescriptionPlace } from './DescriptionPlace';
import { NumberOfSession } from './NumberOfSession';
import { Beneficiaries } from './Beneficiaries/Beneficiaries';
import { ContactPerson } from './ContactPerson';
import { convertCoords4326To3857, convertCoords3857To4326 } from 'utils/Helper';

export const SessionInfo = (props) => {
  const {
    formData,
    setFormData,
    handouts,
    errors,
    zone,
    setZone,
  } = props;
  const [pointExist, setPointExist] = useState(false);
  const [coords3857, setCoords3857] = useState([null, null]);
  const [map, setMap] = useState(null);
  const haReportsEORECreate = useHAReports(map);
  const initializedRef = useRef(false);
  const manuallySetRef = useRef(false);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      point: formData?.info?.point,
    }));
  }, [formData?.info?.point]);

  function hasTwoDigits(arr) {
    return arr?.some((num) => (num >= 10 && num <= 99) || (num <= -10 && num >= -99));
  }

  function isUTMCoordinates(arr) {
    return arr?.some((num) => Math.abs(num) > 100000);
  }

  const setCoords3857WithTracking = (coords) => {
    if (typeof coords === 'function') {
      setCoords3857((prev) => {
        const newCoords = coords(prev);
        if (newCoords[0] !== null && newCoords[1] !== null) {
          manuallySetRef.current = true;
          initializedRef.current = true;
          setPointExist(true);
        }
        return newCoords;
      });
    } else {
      setCoords3857(coords);
      if (coords[0] !== null && coords[1] !== null) {
        manuallySetRef.current = true;
        initializedRef.current = true;
        setPointExist(true);
      }
    }
  };

  useEffect(() => {
    const prop = formData?.info?.point?.coordinates;

    if (prop && prop[0] && prop[1] && !initializedRef?.current) {
      if (formData.point?.coordinates && formData.point.coordinates[0] && formData.point.coordinates[1]) {
        if (isUTMCoordinates(formData.point.coordinates)) {
          setCoords3857(formData.point.coordinates);
        } else {
          const coords3857 = convertCoords4326To3857(formData.point.coordinates, zone);
          if (coords3857) {
            setCoords3857(coords3857);
          }
        }
      } else {
        const coords3857 = convertCoords4326To3857(prop, zone);
        if (coords3857) {
          setCoords3857(coords3857);
        }
      }
      initializedRef.current = true;
      setPointExist(true);
    }
  }, [zone, formData?.info?.point?.coordinates, initializedRef?.current]);

  useEffect(() => {
    const settlementName = formData?.info?.session_information?.settlement_name;
    if (settlementName && !manuallySetRef.current) {
      setPointExist(false);
      initializedRef.current = false;
      setCoords3857([null, null]);
    }
  }, [formData?.info?.session_information?.settlement_name]);

  useEffect(() => {
    if (zone && formData.zone !== zone) {
      setFormData((prev) => ({
        ...prev,
        zone: zone,
      }));
    }
  }, [zone, formData?.zone]);

  useEffect(() => {
    if (manuallySetRef.current && coords3857[0] !== null && coords3857[1] !== null && zone) {
      const coords4326 = convertCoords3857To4326(coords3857, zone);
      if (coords4326 && coords4326[0] !== null && coords4326[1] !== null) {
        setFormData((prev) => {
          const currentCoords = prev?.info?.point?.coordinates;
          if (currentCoords &&
              Math.abs(currentCoords[0] - coords4326[0]) < 0.01 &&
              Math.abs(currentCoords[1] - coords4326[1]) < 0.01) {
            return prev;
          }
          return {
            ...prev,
            info: {
              ...prev?.info,
              point: {
                type: 'Point',
                coordinates: coords4326,
              },
            },
          };
        });
      }
    }
  }, [coords3857, zone, manuallySetRef.current]);

  return (
    <>
      <AccordionItemHeading>
        <AccordionItemButton>
          <AccordionHeaderTitle
            accordionId="2"
            validationForm={props?.validationForm}
            block="sessionInformation"
            titleUA={(
              <div className="flex-group">
                <span>Інформація щодо сесії</span>
                <span className="star">*</span>
              </div>
            )}
            titleEN="Information about the session"
          />
        </AccordionItemButton>
      </AccordionItemHeading>
      <AccordionItemPanel>
        <LocationComponent
          fieldBlock="session_information"
          formData={formData}
          setFormData={setFormData}
          setCoords3857={pointExist ? () => {} : setCoords3857WithTracking}
          setZone={pointExist ? () => {} : setZone}
          pointExist={pointExist}
          setPointExist={setPointExist}
          errors={errors}
        />
        <SettlementId
          settlementId={props?.formData?.info?.session_information?.settlement_code}
        />
        <NTSmap
          map={map}
          setMap={setMap}
          formData={formData}
          setFormData={setFormData}
          coords3857={coords3857}
          setCoords3857={setCoords3857WithTracking}
          zone={zone}
          errors={props?.errors}
          mode="edit"
          haReportsNonTechnical={haReportsEORECreate}
        />
        <SessionDate
          formData={formData}
          setFormData={setFormData}
          errors={props?.errors}
        />
        <div className="grey-wrapper">
          <TypeOfSessionInternal
            formData={formData}
            setFormData={setFormData}
            errors={props?.errors}
          />
          <TypeOfSessionImsma
            formData={formData}
            setFormData={setFormData}
            errors={props?.errors}
          />
          <PlaceTypeOfInternal
            formData={formData}
            setFormData={setFormData}
            errors={props?.errors}
          />
          <StateInstitution
            formData={formData}
            setFormData={setFormData}
            errors={props?.errors}
          />
          <EducationFacility
            formData={formData}
            setFormData={setFormData}
            errors={props?.errors}
          />
          <CriticalInfrastructure
            formData={formData}
            setFormData={setFormData}
            errors={props?.errors}
          />
          <PlaceTypeOfImsma
            formData={formData}
            setFormData={setFormData}
            errors={props.errors}
          />
          <DescriptionPlace
            formData={formData}
            setFormData={setFormData}
            errors={props?.errors}
          />
          <NumberOfSession
            formData={formData}
            setFormData={setFormData}
            errors={props?.errors}
          />
          <Beneficiaries
            formData={formData}
            setFormData={setFormData}
            handouts={handouts}
          />
          <ContactPerson
            formData={formData}
            setFormData={setFormData}
          />
        </div>
      </AccordionItemPanel>
    </>
  );
};
